import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'

import { ApiUrlPath, IQuestion } from 'cat-shared/utils'
import { AppButton, AppFlexContainer, AppFlexItem } from 'client/ui-building-blocks'
import { useAppContext, usePage, usePageStateContext } from 'client/hooks'
import { displaySnackBar } from 'client/services'
import { useFollowQuestion, useUnfollowQuestion } from 'client/api'
import { type TGetQuestionData } from 'client/api/useGetQuestion/useGetQuestion'

type Props = {
  question: IQuestion
  disabled: boolean
}

export const QuestionMetaBtns = ({ question, disabled = false }: Props) => {
  const { t } = useTranslation()
  const { pathname } = useLocation()
  const queryClient = useQueryClient()

  const { navigateTo } = usePage()
  const { authUser } = useAppContext()
  const { showQuestionVoteStats, toggleQuestionVoteStats } = usePageStateContext()

  const { mutateAsync: followQuestion, isLoading: isLoadingFollowQuestion } =
    useFollowQuestion()

  const { mutateAsync: unfollowQuestion, isLoading: isLoadingUnfollowQuestion } =
    useUnfollowQuestion()

  const onFollowBtnClick = () => {
    if (isFollowable) {
      if (!question.isRequestorFollowing) {
        followQuestion({ questionId: String(question?._id) })
          .then(() => {
            queryClient.setQueryData(
              [ApiUrlPath.GetQuestion, question?._id.toString()],
              (data: TGetQuestionData) => {
                return {
                  ...data,
                  data: [{ ...data.data[0], isRequestorFollowing: true }],
                }
              }
            )
          })
          .catch(err => {
            if (err?.msg) {
              displaySnackBar(err.msg)
              handleClose()
            }
          })
      } else {
        unfollowQuestion({ questionId: String(question?._id) })
          .then(() => {
            queryClient.setQueryData(
              [ApiUrlPath.GetQuestion, question?._id.toString()],
              (data: TGetQuestionData) => {
                return {
                  ...data,
                  data: [{ ...data.data[0], isRequestorFollowing: false }],
                }
              }
            )
          })
          .catch(err => {
            if (err?.msg) {
              displaySnackBar(err.msg)
              handleClose()
            }
          })
      }
    }
  }

  const handleClose = () => {
    navigateTo({ path: pathname, preventScroll: true })
  }

  const isFollowable =
    authUser && authUser.config?.confirmed && authUser._id !== question?.creatorId

  const onToggleStatsBtnClick = useCallback(() => {
    toggleQuestionVoteStats(!showQuestionVoteStats)
  }, [showQuestionVoteStats])

  return (
    <AppFlexContainer rowGap="5px" columnGap="5px">
      {isFollowable && (
        <AppFlexItem>
          <AppButton
            text={
              question?.isRequestorFollowing
                ? t('question.buttons.unfollow')
                : t('question.buttons.follow')
            }
            iconName={question?.isRequestorFollowing ? 'CHECK' : undefined}
            iconPosition="right"
            loading={isLoadingFollowQuestion || isLoadingUnfollowQuestion}
            disabled={disabled || !question?.answeredStats}
            onClick={onFollowBtnClick}
            variant={question?.isRequestorFollowing ? 'success' : 'outlined'}
          />
        </AppFlexItem>
      )}
      <AppFlexItem>
        <AppButton
          variant={showQuestionVoteStats ? 'success' : 'outlined'}
          text="Toggle details"
          iconName={showQuestionVoteStats ? 'CHECK' : undefined}
          iconPosition="right"
          onClick={onToggleStatsBtnClick}
          disabled={disabled || !question?.answeredStats}
        />
      </AppFlexItem>
    </AppFlexContainer>
  )
}
